/**
 * Created by Ning Jiang on 2021-05-04.
 */

import {DeadwoodRipParsedData} from "./DeadwoodRipParsedData";
import {IBetWinData, IBulletData, IDeadwoodRipGameData} from "./IDeadwoodRipData";
import {DeadwoodRipDataParser} from "./DeadwoodRipDataParser";
import {IWinRatioConfig} from "../../../core/config/WinRatio";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/utils/ArrayHelper";
import {StackedSymbol} from "../../../core/reel/stackedsymbol/StackedSymbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {DeadwoodRipReelController} from "../../reels/DeadwoodRipReelController";

export class DeadwoodRipParsedGameData extends DeadwoodRipParsedData {

    public readonly xRip:boolean;
    public readonly singleWin:number;
    public readonly playedBetValue:number;
    public readonly wasFeatureBuy:boolean;
    public readonly brokeTheBank:boolean;
    public readonly nAdditionalFsAwarded:number;
    public readonly bulletDataList:IBulletData[];

    // Calculated values
    public betWins:IBetWinData[];
    public betWinsBeforeReveal:IBetWinData[];
    public winPositions:boolean[][];
    public readonly transformScatterToReelSplit:number[];
    public readonly reelSplit:boolean[];
    public readonly doXSplit:boolean;
    public readonly lastReelEnabled:boolean;
    public readonly currentWinRatioConfig:IWinRatioConfig | null;
    public readonly cylinderRow:number;
    public readonly evaluatedArea:string[][];

    // To add Spin Data
    public readonly nearWinReels:boolean[];
    public readonly nearWinBigWinReels:boolean[];

    // To add Nudge Data
    public readonly nudgeSteps:number[];


    constructor(rawData:IDeadwoodRipGameData, parser:DeadwoodRipDataParser) {
        super(rawData, parser);

        this.xRip = rawData.xRip;
        this.singleWin = rawData.totalSpinWinnings;
        this.playedBetValue = rawData.playedBetValue;
        this.wasFeatureBuy = rawData.wasFeatureBuy;
        this.brokeTheBank = rawData.brokeTheBank;
        this.nAdditionalFsAwarded = rawData.nAdditionalFsAwarded;
        this.bulletDataList = rawData.bulletDataList.concat();
        this.evaluatedArea = rawData.evaluatedArea;

        this.nudgeSteps = this.parseNudgeSteps();
        this.transformScatterToReelSplit = parser.parseTransformScatterToReelSplit(this.S1Positions, this.mode);
        this.reelSplit = parser.parseReelSplit(rawData, this.transformScatterToReelSplit);
        this.doXSplit = (this.reels[4][1] === "S2*0" && this.reels[4][2] === "S2*1") || this.mode === GameMode.FREESPIN_SUPER;
        this.lastReelEnabled = this.parseLastReelEnabled(rawData);
        this.cylinderRow = this.parseCylinderRow(rawData);

        this.parseBetWayWins(rawData);
        this.currentWinRatioConfig = parser.parseWinRatio(this.singleWin / this.playedBetValue);

        this.nearWinReels = parser.parseNearWinReels(this.S1Positions, this.wasFeatureBuy, this.mode);
        this.nearWinBigWinReels = this.getRandomBigWinNearWin();
    }

    private parseLastReelEnabled(rawData:IDeadwoodRipGameData):boolean {
        if(this.doXSplit) {
            return true;
        }

        return rawData.reels[4][1] == rawData.reels[4][2];
    }

    private parseCylinderRow(rawData:IDeadwoodRipGameData):number {
        if(rawData.bulletDataList && rawData.bulletDataList.length > 0) {
            const reelConfig = DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[0];
            const symStartId = reelConfig[1];
            for(let symId = symStartId; symId < symStartId + reelConfig[2]; symId++) {
                if(rawData.reels[0][symId] === "C") {
                    return symId - symStartId;
                }
            }
        }

        return -1;
    }

    public parseBetWayWins(rawData:IDeadwoodRipGameData):void {
        this.betWins = [];
        this.betWinsBeforeReveal = [];
        this.winPositions = ArrayHelper.initArrayWithValues<boolean[]>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
            return ArrayHelper.initArrayWithValues<boolean>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
        });
        rawData.betWayWins!.forEach(rawWin => {
            const betWin = {
                symbol : rawWin.symbol,
                amount : rawWin.amount,
                multiplier : rawWin.multiplier,
                multipliedAmount : rawWin.multipliedAmount,
                length : rawWin.length,
                numberOfCombinations : rawWin.numberOfCombinations,
                reelWinPositions : ArrayHelper.initArrayWithValues<boolean[]>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
                    return ArrayHelper.initArrayWithValues<boolean>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
                })
            };
            const betWinBeforeReveal = {
                symbol : rawWin.symbol,
                amount : rawWin.amount,
                multiplier : rawWin.multiplier,
                multipliedAmount : rawWin.multipliedAmount,
                length : rawWin.length,
                numberOfCombinations : rawWin.numberOfCombinations,
                reelWinPositions : ArrayHelper.initArrayWithValues<boolean[]>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
                    return ArrayHelper.initArrayWithValues<boolean>(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId][2], () => false)
                })
            };
            for(let reelId = 0; reelId < rawWin.length; reelId++) {
                const reel = rawData.reels[reelId];
                const reelConfig = DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId];
                const symStartId = reelConfig[1];
                for(let symId = symStartId; symId < symStartId + reelConfig[2]; symId++) {
                    if(reel[symId].includes(rawWin.symbol) || reel[symId].includes("W") || reel[symId].includes("S2")) {
                        betWin.reelWinPositions[reelId][symId - symStartId] = true;
                        this.winPositions[reelId][symId - symStartId] = true;
                        betWinBeforeReveal.reelWinPositions[reelId][symId - symStartId] = true;
                    }
                    if(this.nudgeSteps[reelId] != 0) { // add full reel to win when nudge
                        betWin.reelWinPositions[reelId][symId - symStartId] = true;
                        this.winPositions[reelId][symId - symStartId] = true;
                    }
                    if(rawData.evaluatedArea[reelId][symId - symStartId].includes("W")) {
                        betWin.reelWinPositions[reelId][symId - symStartId] = true;
                        this.winPositions[reelId][symId - symStartId] = true;
                    }
                }
                if(this.transformScatterToReelSplit[reelId] > -1) {
                    const symId = this.transformScatterToReelSplit[reelId];
                    betWin.reelWinPositions[reelId][symId] = true;
                    this.winPositions[reelId][symId] = true;
                }
            }

            // if any revealed feature, update betWin, betWinBeforeReveal and winPositions.

            this.betWins.push(betWin);

            // Remove the betWinBeforeReveal if it only win with revealed symbol before reel 3.
            for(let rId = 0; rId < betWinBeforeReveal.length; rId++) {
                if(betWinBeforeReveal.reelWinPositions[rId].every((w) => !w)) {
                    if(rId < 3) {
                        return;
                    }
                    rId++;
                    while(rId < betWinBeforeReveal.length) {
                        betWinBeforeReveal.reelWinPositions[rId] = [false, false, false, false];
                        rId++;
                    }
                    break;
                }
            }
            this.betWinsBeforeReveal.push(betWinBeforeReveal);
        });

        // add scatter positions to allWinPositions.
        if(this.freespinTriggeredThisSpin || this.nAdditionalFsAwarded > 1) {
            this.S1Positions.forEach((symId, reelId) => {
                if(symId > -1) {
                    this.winPositions[reelId][symId] = true;
                }
            })
        }

        if(this.doXSplit) {
            this.winPositions[4][0] = true;
            this.winPositions[4][1] = true;
        }

        if(this.cylinderRow > -1) {
            this.winPositions[0][this.cylinderRow] = true;
        }

        if(rawData.lowTransformList && rawData.lowTransformList.length > 0) {
            this.winPositions[1][this.S3Positions[1]] = true;
            this.winPositions[3][this.S3Positions[3]] = true;
        }
    }

    private getRandomBigWinNearWin():boolean[] {
        const values = ArrayHelper.initArrayWithValues(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA.length, () => false);
        if(this.mode != GameMode.FREESPIN_SUPER) {
            values[4] = this.getRandomBigWinTease();
        }

        return values;
    }

    private getRandomBigWinTease():boolean {
        if(this.wasFeatureBuy) {
            return false;
        }
        // if(this.totalWin === 0 || this.reels[4][1] === "S2") {
        //     return false;
        // }
        switch(this.mode) {
            case GameMode.NORMAL:
                return this.currentWinRatioConfig!.ratio + Math.random() * 15 > 35;
            case GameMode.FREESPIN:
                return this.currentWinRatioConfig!.ratio + Math.random() * 20 > 50;
            // case SerialGameMode.FREESPIN_SUPER:
            //     return false; // super fs reel 5 stop the first!
            default:
                return false;
        }
    }

    // The range is the start index to the end index of visible symbols in the reel data.
    // For a 3x5 slot, there are 3 symbols in reel data, then the range is [0, 3],
    // If you have a special feature that there are actually 5 symbols but you only show 3 symbols from the bottom of the 5 symbols, then you should return [2, 4].
    private parseNudgeSteps():number[] {
        return ArrayHelper.initArrayWithValues(DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA.length, reelId => {
            if(reelId < 1 || reelId > 3) { // TODO: change numbers for different reelsets
                return 0;
            }
            const reelConfig = DeadwoodRipReelController.SYMBOLS_NUM_IN_DATA[reelId];
            const checkRange:[number, number] = [reelConfig[1], reelConfig[1] + reelConfig[2]];

            let symbolIndex:number = -1;
            let stackIndex:number = -1;

            for(let i:number = checkRange[0]; i < checkRange[1]; i++) {
                const stackedSymbolName = StackedSymbol.parseStackedSymbolName(this.reels[reelId][i], reelId);
                if(stackedSymbolName.symName === "WXN") {
                    symbolIndex = i;
                    stackIndex = stackedSymbolName!.index;
                    break;
                }
            }

            if(symbolIndex === -1) {
                return 0;
            }

            return checkRange[0] + stackIndex - symbolIndex;
        });
    }
}